// QuotePreview.jsx — the real client quote, live, inside the calculators.
// Built by generateQuoteDocumentHTML from a stand-in log row made of the
// calculator's current save state, so it is exactly what View Full Quote
// will show once saved. Nothing here saves anything.

import React, { useEffect, useRef, useState } from "react";
import { generateQuoteDocumentHTML } from "./generateQuoteDocument";

// Printed where the quote number goes until the quote has one.
export const PREVIEW_REF = "Assigned on save";

const fmtMoney = n => "£" + Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// category: "Automotive", or the Industrial category label (as saved to the
// log). quoteNumber: the saved number, only while the calculator still
// matches what was saved; empty otherwise (unsaved, or changed since).
export default function QuotePreview({ category, state, totalExVat, quoteNumber, t }) {
  const [html, setHtml] = useState("");
  const [error, setError] = useState("");
  const [height, setHeight] = useState(900);
  const frameRef = useRef(null);
  const ref = quoteNumber || PREVIEW_REF;
  // The save state is a new object every render; rebuild only when its
  // content changes, and not on every keystroke.
  const key = JSON.stringify({ category, state, totalExVat, ref });

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const row = {
          "Quote Number": ref,
          "Category": category,
          "Date Created": "",
          "Total (ex VAT)": fmtMoney(totalExVat),
          "State JSON": JSON.stringify({ ...state, job: { ...(state?.job || {}), quoteRef: ref } }),
        };
        const out = await generateQuoteDocumentHTML(row);
        if (!cancelled) { setHtml(out); setError(""); }
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Size the frame to its page so the calculator column scrolls, not the frame.
  const fit = () => {
    try {
      const doc = frameRef.current?.contentDocument;
      if (doc?.body) setHeight(Math.max(400, doc.documentElement.scrollHeight));
    } catch (e) { /* not readable: keep the last height */ }
  };

  return (
    <div style={{ marginTop: 32 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", color: t.textMuted }}>Client Quote</div>
        {quoteNumber ? (
          <span style={{ fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 999, color: t.success, border: `1px solid ${t.success}` }}>
            Saved as {quoteNumber}
          </span>
        ) : (
          <span style={{ fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 999, background: "rgba(245,166,35,0.15)", color: t.warning, border: `1px solid ${t.warning}` }}>
            Preview, not saved
          </span>
        )}
      </div>
      <div style={{ borderRadius: 10, overflow: "hidden", border: quoteNumber ? `1px solid ${t.border}` : `2px dashed ${t.warning}`, background: "#fff" }}>
        {error ? (
          <div style={{ padding: 20, fontSize: 12, color: "#a33" }}>The preview couldn't be built: {error}</div>
        ) : (
          <iframe
            ref={frameRef}
            title="Client quote preview"
            srcDoc={html}
            onLoad={fit}
            // Same-origin so the height can be read; no scripts run in it.
            sandbox="allow-same-origin"
            style={{ display: "block", width: "100%", height, border: "none", background: "#fff" }}
          />
        )}
      </div>
      <div style={{ fontSize: 11, color: t.textDim, marginTop: 8 }}>
        Updates as you change the calculator. This is what the client will see on the PDF and the quote link once it's saved.
      </div>
    </div>
  );
}
